<template>
  <div class="seat-layout">
    <div class="mb-6">
      <div v-if="mode == 'Create' || mode == 'Edit'" class="flex flex-wrap justify-center gap-4 text-sm">
        <div class="flex items-center gap-2">
          <div class="seat seat-available mr-2"></div>
          <span class="whitespace-nowrap">자리있음</span>
        </div>
        <div class="flex items-center gap-2">
          <div class="seat seat-disabled mr-2"></div>
          <span class="whitespace-nowrap">자리없음</span>
        </div>
        <div v-if="mode == 'Edit'" class="flex items-center gap-2">
          <div class="seat seat-reserved mr-2"></div>
          <span class="whitespace-nowrap">예약됨</span>
        </div>
      </div>
      <div v-else class="flex flex-wrap justify-center gap-4 text-sm">
        <div class="flex items-center gap-2">
          <div class="seat seat-available mr-2"></div>
          <span class="whitespace-nowrap">예약가능</span>
        </div>
        <div class="flex items-center gap-2">
          <div class="seat seat-reserved mr-2"></div>
          <span class="whitespace-nowrap">예약됨</span>
        </div>
        <div v-if="mode == 'Detail'" class="flex items-center gap-2">
          <div class="seat seat-my-reserved mr-2"></div>
          <span class="whitespace-nowrap">내예약</span>
        </div>
        <div v-if="mode == 'Reserve'" class="flex items-center gap-2">
          <div class="seat seat-selected mr-2"></div>
          <span class="whitespace-nowrap">선택됨</span>
        </div>
        <div class="flex items-center gap-2">
          <div class="seat seat-disabled mr-2"></div>
          <span class="whitespace-nowrap">자리없음</span>
        </div>
      </div>
    </div>

    <div class="seat-grid">
      <div
        v-for="(row, rowIndex) in seatGrid"
        :key="rowIndex"
        class="seat-row"
      >
        <div class="row-label">{{ String.fromCharCode(65 + rowIndex) }}</div>
        <div
          v-for="(seat, seatIndex) in row"
          :key="seatIndex"
          class="seat"
          :class="{
            'seat-available': !seat.isReserved && !seat.isSelected && seat.isActive,
            'seat-reserved': seat.isReserved && !seat.isSelected && seat.isActive,
            'seat-my-reserved': seat.isReserved && !seat.isSelected && seat.isActive && seat.isMe,
            'seat-disabled': !seat.isActive,
            'seat-selected': !seat.isReserved && seat.isSelected && seat.isActive,
            'cursor-pointer': !seat.isReserved && seat.isActive && !readonly,
            'cursor-not-allowed': seat.isReserved || !seat.isActive || seat.isSelected || readonly
          }"
          @click="handleSeat(seat)"
        >
          {{ seat.number != -1 ? seat.number : ""}}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import type { SeatLayoutInfo } from '@/types'
import { useToast } from 'vue-toastification'

interface Props {
  layout: SeatLayoutInfo
  reservedSeatNumbers?: number[]
  mySeatNumbers?: number[]
  readonly?: boolean
  selectedSeats?: number[]
  mode?: "Create" | "Detail" | "Edit" | "Reserve"
  maxSelectableSeats?: number
  hasActiveReservations?: boolean
}

interface SeatInfo {
  number: number
  row: number
  seatsPerRow: number
  isReserved: boolean
  isSelected: boolean
  isActive: boolean
  isMe: boolean
}

const props = withDefaults(defineProps<Props>(), {
  reservedSeatNumbers: () => [],
  mySeatNumbers: () => [],
  readonly: false,
  selectedSeats: () => [],
  mode: () => "Create",
  maxSelectableSeats: 1,
  hasActiveReservations: false
})

const emit = defineEmits<{
  handleSeatLayout: [seatLayout: SeatLayoutInfo, seat: SeatInfo]
}>()

const seatGrid = ref<SeatInfo[][]>([])
const toast = useToast()

// 좌석 배치 파싱
const generateGrid = (
    rows: number,
    seatsPerRow: number,
    seatStatus: number[][] | null
): SeatInfo[][] => {
  const grid: SeatInfo[][] = []
  let seatNumber = 1

  for (let row = 0; row < rows; row++) {
      const rowSeats: SeatInfo[] = []
    for (let seat = 0; seat < seatsPerRow; seat++) {
      const isReserved = props.reservedSeatNumbers.includes(seatNumber)
      const isMe = props.mySeatNumbers.includes(seatNumber)
      const isSelected = props.selectedSeats.includes(seatNumber)
      const isActive = seatStatus ? seatStatus[row][seat] === 1 : true

      rowSeats.push({
        number: isActive ? seatNumber : -1,
        row,
        seatsPerRow: seat,
        isReserved,
        isSelected,
        isActive,
        isMe,
      })

      if (isActive) {
        seatNumber++
      }
    }
    grid.push(rowSeats)
  }

  return grid
}

const parseSeatLayout = () => {
  try {
    const layoutData = props.layout
    const rows = layoutData.rows || 5
    const seatsPerRow = layoutData.seatsPerRow || 8
    const seatStatus = layoutData.seatStatus

    seatGrid.value = generateGrid(rows, seatsPerRow, seatStatus)
  } catch (error) {
    // seatStatus가 없으면 기본값(true)으로 생성
    seatGrid.value = generateGrid(5, 8, null)
  }
}

// 좌석 클릭 이벤트
const handleSeat = (seat: SeatInfo) => {
  if (props.readonly) {
    return
  }
  if (seat.isReserved) {
    return
  }
  if (props.mode === "Edit" && props.hasActiveReservations) {
    toast.error("이미 예약이 진행된 상태이기 때문에 좌석 배치를 수정할 수 없습니다.")
    return
  }
  props.mode === "Reserve" ? toggleSeatReserve(seat) : toggleSeat(seat)
}
// 좌석 선택 토글 (생성, 수정)
const toggleSeat = (seat: SeatInfo) => {
  console.log("SeatLayout > toggleSeat", seat)
  if (seat.isReserved) return
  seat.isActive = !seat.isActive
  props.layout.seatStatus[seat.row][seat.seatsPerRow] = seat.isActive ? 1 : 0
  props.layout.totalSeats = seat.isActive ? props.layout.totalSeats+1 : props.layout.totalSeats-1
  parseSeatLayout()
  emit('handleSeatLayout', props.layout, seat)
}
// 좌석 선택 토글 (예약)
const toggleSeatReserve = (seat: SeatInfo) => {
  console.log("SeatLayout > toggleSeatReserve", seat)
  if (seat.isReserved) return
  if (!seat.isActive) return
  if (!seat.isSelected && props.selectedSeats.length >= props.maxSelectableSeats) {
    toast.error(`최대 ${props.maxSelectableSeats}좌석까지 선택할 수 있습니다.`)
    return
  }
  seat.isSelected = !seat.isSelected
  if (seat.isSelected) {
    if (!props.selectedSeats.includes(seat.number)) {
      props.selectedSeats.push(seat.number)
    }
  } else {
    const index = props.selectedSeats.indexOf(seat.number)
    if (index !== -1) {
      props.selectedSeats.splice(index, 1)
    }
  }
  parseSeatLayout()
  emit('handleSeatLayout', props.layout, seat)
}

onMounted(() => {
  parseSeatLayout()
})
</script>

<style scoped>
.seat-layout {
  @apply w-full max-w-2xl mx-auto;
}

.seat-grid {
  @apply bg-gray-50 rounded-lg p-6;
}

.seat-row {
  @apply flex items-center justify-center mb-3;
}

.row-label {
  @apply w-8 text-center font-semibold text-gray-600 mr-4;
}

.seat {
  @apply w-8 h-8 mx-1 rounded-md border-2 flex items-center justify-center text-xs font-medium transition-all;
}

.seat-available {
  @apply bg-green-100 border-green-300 text-green-800 hover:bg-green-200;
}

.seat-reserved {
  @apply bg-red-100 border-red-300 text-red-800;
}

.seat-my-reserved {
  @apply bg-yellow-100 border-yellow-300 text-yellow-800;
}

.seat-selected {
  @apply bg-blue-100 border-blue-300 text-blue-800;
}

.seat-disabled {
  @apply bg-gray-100 border-gray-300 text-gray-800;
}
</style>
